import React, { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory } from 'react-router';
import { getDisciplines } from '../../../api/services/disciplineService';
import { getStudentStatuses } from '../../../api/services/studentStatusService';
import { getEntryTypesForTaskType } from '../../../api/services/entryTypeService';
import { getTaskType } from '../../../api/services/taskTypeService';
import { FieldInput } from '../../../components/fieldInput';
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import { useParams } from 'react-router-dom';
import LayoutError from '../../../components/layoutError';
import cx from 'classnames';
import Modal from '../../../components/modal';
import {
  deleteTaskType,
  updateTaskType,
} from '../../../api/services/taskTypeService';
import { ResponseStatus } from '../../../components/notifications';
import TaskTypeOption from '../../../api/models/tasktypeoption';

type Params = {
  id: string;
};

type OptionWithKey = {
  id: number;
  taskTypeOptionId?: number;
  name: string;
};

const fetchTaskType = async ({ id }: any) => {
  return await getTaskType(id);
};

const EditTaskTypes = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  // State
  const [taskTypeId, setTaskTypeId] = useState<number | undefined>();
  const [name, setName] = useState('');
  const [notesRequired, setNotesRequired] = useState(false);
  const [visible, setVisible] = useState(false);
  const [recoupment, setRecoupment] = useState(false);
  const [makeupTime, setMakeupTime] = useState(false);
  const [entryTypes, setEntryTypes] = useState<(number | undefined)[]>([]);
  const [studentStatuses, setStudentStatuses] = useState<
    (number | undefined)[]
  >([]);
  const [disciplines, setDisciplines] = useState<number[]>([]);
  const [modalDisciplines, setModalDisciplines] = useState<number[]>([]);
  const [openDisciplines, setOpenDisciplines] = useState(false);
  const [openOptions, setOpenOptions] = useState(false);
  const [optionName, setOptionName] = useState('');
  const [optionsWithKeys, setOptionsWithKeys] = useState<OptionWithKey[]>([]);
  const [allowWhenAbsent, setAllowWhenAbsent] = useState<boolean>(false)
  const [allowWhenUnavailable, setAllowWhenUnavailable] = useState<boolean>(false)

  // Data
  const taskTypeData = useAsync({
    promiseFn: fetchTaskType,
    id,
    onResolve: (r) => {
      setTaskTypeId(r.data.taskType.taskTypeId);
      setName(r.data.taskType.name);
      setNotesRequired(r.data.taskType.areNotesRequired);
      setVisible(r.data.taskType.isVisible);
      setRecoupment(r.data.taskType.hasRecoupment);
      setMakeupTime(r.data.taskType.hasMakeupTime ?? false);
      setAllowWhenAbsent(r.data.taskType.allowWhenAbsent ?? false);
      setAllowWhenUnavailable(r.data.taskType.allowWhenUnavailable ?? false);
      setStudentStatuses(r.data.taskType.taskTypeStudentStatuses.map((d) => d.studentStatusId));
      setDisciplines(r.data.taskType.taskTypeDisciplines.map((d) => d.disciplineId));
      setEntryTypes(r.data.taskType.taskTypeEntryTypes.map((d) => d.entryTypeId));
      let options: OptionWithKey[] = [];
      for (let i = 0; i < r.data.taskType.taskTypeOptions.length; i++) {
        let a: OptionWithKey = {
          id: i + 1,
          taskTypeOptionId: r.data.taskType.taskTypeOptions[i].taskTypeOptionId,
          name: r.data.taskType.taskTypeOptions[i].name,
        };
        options.push(a);
      }
      setOptionsWithKeys(options);
    },
  });

  const disciplineData = useAsync({ promiseFn: getDisciplines });
  const studentStatusData = useAsync({ promiseFn: getStudentStatuses });
  const entryTypeData = useAsync({ promiseFn: getEntryTypesForTaskType });

  function getOptions() {
    let options: TaskTypeOption[] = [];
    for (let i = 0; i < optionsWithKeys.length; i++) {
      let a: TaskTypeOption = {
        taskTypeOptionId: optionsWithKeys[i].taskTypeOptionId,
        name: optionsWithKeys[i].name,
      };
      options.push(a);
    }
    return options;
  }

  const saveTaskType = async () => {
    if (!taskTypeId) {
      return Promise.reject('Missing taskTypeId.');
    }

    return await updateTaskType(taskTypeId, {
      name: name,
      areNotesRequired: notesRequired,
      isVisible: visible,
      hasRecoupment: recoupment,
      hasMakeupTime: makeupTime,
      entryTypeIds: entryTypes,
      disciplineIds: disciplines,
      studentStatusIds: hasStudentEntryType ? studentStatuses : [],
      taskTypeOptions: getOptions(),
      allowWhenAbsent: allowWhenAbsent,
      allowWhenUnavailable: allowWhenUnavailable
    });
  };

  const save = useAsync({
    deferFn: saveTaskType,
    onResolve: () => {
      history.push('/admin/task-types', {
        message: `"${name}" saved successfully!`,
      });
    },
    onReject: () => {
      window.scrollTo(0, 0);
    },
  });

  const removeTaskType = async () => {
    if (!taskTypeId) {
      return Promise.reject('Missing taskTypeId.');
    }

    return await deleteTaskType(taskTypeId);
  };

  const handleSubmit = () => {
    save.run();
  };

  // Error handling
  if (taskTypeData.error || disciplineData.error) return <LayoutError />;

  // Validation
  var isValidOption = optionName.length > 0;
  var exists = optionsWithKeys.some((v) => v.name === optionName);

  // Handlers
  const handleStudentStatusChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.currentTarget.checked) {
      setStudentStatuses([...studentStatuses, parseInt(e.currentTarget.value)]);
    } else {
      setStudentStatuses([
        ...studentStatuses.filter((t) => t !== parseInt(e.currentTarget.value)),
      ]);
    }
  };

  const handleEntryTypeChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.currentTarget.checked) {
      setEntryTypes([...entryTypes, parseInt(e.currentTarget.value)]);
    } else {
      setEntryTypes([
        ...entryTypes.filter((t) => t !== parseInt(e.currentTarget.value)),
      ]);
    }
  };

  const handleRemoveOption = (id?: number) => {
    if (!id) return;

    setOptionsWithKeys([...optionsWithKeys.filter((d) => d?.id !== id)]);
  };

  const handleAddOption = () => {
    const maxId = Math.max(...optionsWithKeys.map((o) => o.id), 0);
    setOptionsWithKeys([
      ...optionsWithKeys,
      {
        id: maxId + 1,
        name: optionName,
      },
    ]);
  };

  const handleRemoveDiscipline = (id?: number) => {
    if (!id) return;

    setDisciplines([...disciplines.filter((d) => d !== id)]);
  };

  const handleModalDisciplineChange = (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    if (e.currentTarget.checked) {
      setModalDisciplines([
        ...modalDisciplines,
        parseInt(e.currentTarget.value),
      ]);
    } else {
      setModalDisciplines([
        ...modalDisciplines.filter(
          (d) => d !== parseInt(e.currentTarget.value)
        ),
      ]);
    }
  };

  // Misc
  const SelectedDisciplines =
    disciplineData.data?.data.disciplines.filter((d) =>
      disciplines.includes(d.disciplineId ?? -1)
    ) ?? [];

  const studentEntryTypeId = entryTypes.find((e) => e === 1) ?? -1;
  const hasStudentEntryType = entryTypes.includes(studentEntryTypeId);

  return (
    <Fragment>
      <Modal
        open={openDisciplines}
        onCancel={() => {
          setOpenDisciplines(false);
        }}
        onSave={() => {
          setDisciplines(modalDisciplines);
          setOpenDisciplines(false);
        }}
      >
        <Fragment>
          <div className='flex'>
            <div className='flex'>
              <div className='flex items-center h-5 ml-1'>
                <input
                  name='all-disciplines'
                  type='checkbox'
                  checked={
                    disciplineData.data?.data.disciplines.length ===
                    modalDisciplines.length
                  }
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      const d =
                        disciplineData.data?.data.disciplines.map(
                          (d) => d.disciplineId ?? -1
                        ) ?? [];

                      setModalDisciplines(d);
                    } else {
                      setModalDisciplines([]);
                    }
                  }}
                />
              </div>
              <div className='ml-3 text-sm'>
                <label htmlFor='all-disciplines'>Select All</label>
              </div>
            </div>
          </div>
          <hr className='my-4' />
          <div className="overflow-y-auto max-h-48">
            <div className='flex flex-col'>
              {disciplineData.data?.data.disciplines.map((d, i) => {
                const dip = `discipline_${i}`;

                return (
                  <div key={dip} className='flex items-start py-1'>
                    <div className='flex items-center h-5 ml-1'>
                      <input
                        id={dip}
                        name='discipline'
                        type='checkbox'
                        value={d.disciplineId}
                        checked={modalDisciplines.includes(d.disciplineId ?? -1)}
                        onChange={handleModalDisciplineChange}
                      />
                    </div>
                    <div className='ml-3 text-sm'>
                      <label htmlFor={dip}>{d.name}</label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Fragment>
      </Modal>

      <Modal
        open={openOptions}
        allowSave={isValidOption && !exists}
        onCancel={() => {
          setOpenOptions(false);
        }}
        onSave={() => {
          setOpenOptions(false);
          handleAddOption();
        }}
      >
        <Fragment>
          <div className='flex flex-col'>
            <div className='grid grid-cols-6 gap-6 mb-6'>
              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='option-name'
                  label='Option Name'
                  minLength={1}
                  onChange={(e) => setOptionName(e.target.value)}
                  value={optionName}
                />
                {exists && openOptions && (
                  <p className='mt-2 italic text-red-500'>
                    Option name is already in use.
                  </p>
                )}
              </div>
            </div>
          </div>
        </Fragment>
      </Modal>

      <Layout
        isLoading={
          taskTypeData.isPending ||
          disciplineData.isPending ||
          studentStatusData.isPending
        }
      >
        <Fragment>
          <ResponseStatus state={save} />
          <div className='grid grid-cols-6 gap-6 mb-6'>
            <div className='col-span-6 sm:col-span-6'>
              <FieldInput
                name='tasktype'
                label='Name'
                minLength={1}
                onChange={(e) => setName(e.target.value)}
                value={name}
              />
            </div>
            <hr className='col-span-6 sm:col-span-6' />
            <div className='col-span-6 sm:col-span-6'>
              <div className='flex items-start'>
                <div className='flex items-center h-5'>
                  <input
                    id='require-notes'
                    name='require-notes'
                    type='checkbox'
                    checked={notesRequired}
                    onChange={(e) => setNotesRequired(e.currentTarget.checked)}
                  />
                </div>
                <div className='ml-3 text-sm'>
                  <label htmlFor='require-notes'>Requires Notes</label>
                  <p className='text-gray-500'>
                    When checked, time logged towards this task type will
                    require additional notes.
                  </p>
                </div>
              </div>
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <div className='flex items-start'>
                <div className='flex items-center h-5'>
                  <input
                    id='is-visible'
                    name='is-visible'
                    type='checkbox'
                    checked={visible}
                    onChange={(e) => setVisible(e.currentTarget.checked)}
                  />
                </div>
                <div className='ml-3 text-sm'>
                  <label htmlFor='is-visible'>Is Visible</label>
                  <p className='text-gray-500'>
                    When checked, this task type will be available for selection
                    on the timesheet entry page.
                  </p>
                </div>
              </div>
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <div className='flex items-start'>
                <div className='flex items-center h-5'>
                  <input
                    id='is-recoupment'
                    name='is-recoupment'
                    type='checkbox'
                    checked={recoupment}
                    onChange={(e) => setRecoupment(e.currentTarget.checked)}
                  />
                </div>
                <div className='ml-3 text-sm'>
                  <label htmlFor='is-recoupment'>Has Recoupment</label>
                  <p className='text-gray-500'>
                    When checked, this task type will have recoupment option in
                    the timesheet entry page.
                  </p>
                </div>
              </div>
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <div className='flex items-start'>
                <div className='flex items-center h-5'>
                  <input
                    id='is-makeuptime'
                    name='is-makeuptime'
                    type='checkbox'
                    checked={makeupTime}
                    onChange={(e) => setMakeupTime(e.currentTarget.checked)}
                  />
                </div>
                <div className='ml-3 text-sm'>
                  <label htmlFor='is-makeuptime'>Has Makeup Time</label>
                  <p className='text-gray-500'>
                    When checked, this task type will have a make up time option in
                    the timesheet entry page.
                  </p>
                </div>
              </div>
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <div className='flex items-start'>
                <div className='flex items-center h-5'>
                  <input
                    id='is-recoupment'
                    name='is-recoupment'
                    type='checkbox'
                    checked={allowWhenAbsent}
                    onChange={(e) => setAllowWhenAbsent(e.currentTarget.checked)}
                  />
                </div>
                <div className='ml-3 text-sm'>
                  <label htmlFor='is-recoupment'>Allow Entry When Absent</label>
                  <p className='text-gray-500'>
                    When checked, this will allow timesheet entry for a student even if
                    they have an Absent task on the entry date.
                  </p>
                </div>
              </div>
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <div className='flex items-start'>
                <div className='flex items-center h-5'>
                  <input
                    id='is-recoupment'
                    name='is-recoupment'
                    type='checkbox'
                    checked={allowWhenUnavailable}
                    onChange={(e) => setAllowWhenUnavailable(e.currentTarget.checked)}
                  />
                </div>
                <div className='ml-3 text-sm'>
                  <label htmlFor='is-recoupment'>Allow Entry When Unavailable</label>
                  <p className='text-gray-500'>
                    When checked, this will allow timesheet entry for a student even if
                    they have an Unavailable task on the entry date.
                  </p>
                </div>
              </div>
            </div>
            <hr className='col-span-6 sm:col-span-6' />
            <div className='col-span-6 sm:col-span-6'>
              <label>
                Entry Types<span className='ml-1 text-red-500'>*</span>
              </label>
              <p className='mb-3 text-sm text-gray-500'>
                Select all applicable entry types.
              </p>
              <div className='px-4 py-3 mt-1 bg-white rounded-lg shadow'>
                {entryTypeData.data?.data.entryTypes.map((et, i) => {
                  const etid = `entrytype_${i}`;

                  return (
                    <div key={etid} className='flex items-start py-1'>
                      <div className='flex items-center h-5'>
                        <input
                          id={etid}
                          value={et.entryTypeId}
                          checked={entryTypes.includes(et.entryTypeId)}
                          onChange={handleEntryTypeChange}
                          name='entry-type'
                          type='checkbox'
                        />
                      </div>
                      <div className='ml-3 text-sm'>
                        <label htmlFor={etid}>{et.name}</label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {hasStudentEntryType && (
              <Fragment>
                <div className='col-span-6 sm:col-span-6'>
                  <label>
                    Student Statuses<span className='ml-1 text-red-500'>*</span>
                  </label>
                  <p className='mb-3 text-sm text-gray-500'>
                    Select all applicable student statuses.
                  </p>
                  <div className='px-4 py-3 mt-1 bg-white rounded-lg shadow'>
                    {studentStatusData.data?.data.studentStatuses.map(
                      (ss, i) => {
                        const ssid = `studentstatus_${i}`;

                        return (
                          <div key={ssid} className='flex items-start py-1'>
                            <div className='flex items-center h-5'>
                              <input
                                id={ssid}
                                value={ss.studentStatusId}
                                checked={studentStatuses.includes(
                                  ss.studentStatusId
                                )}
                                onChange={handleStudentStatusChange}
                                name='entry-type'
                                type='checkbox'
                              />
                            </div>
                            <div className='ml-3 text-sm'>
                              <label htmlFor={ssid}>{ss.name}</label>
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
                <hr className='col-span-6 sm:col-span-6' />
              </Fragment>
            )}
            <div className='col-span-6 sm:col-span-6'>
              <div className='flex justify-between mb-3'>
                <div>
                  <label>Disciplines</label>
                  <p className='text-sm text-gray-500'>
                    Select all applicable disciplines.
                  </p>
                </div>
                <div className='flex items-end'>
                  <button
                    className='callout'
                    onClick={() => {
                      setModalDisciplines(disciplines);
                      setOpenDisciplines(true);
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='w-5 h-5'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path d='M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z' />
                    </svg>
                  </button>
                </div>
              </div>
              <div className='mt-1 overflow-hidden bg-white rounded-lg shadow'>
                {(SelectedDisciplines.length === 0 ||
                  disciplines.length ===
                  disciplineData.data?.data.disciplines.length) && (
                    <div className='px-5 py-3'>
                      <div className='text-base font-normal'>Global</div>
                      <p className='text-sm text-gray-500'>
                        Task type will appear for all disciplines.
                      </p>
                    </div>
                  )}

                {SelectedDisciplines.length !== 0 &&
                  disciplines.length !==
                  disciplineData.data?.data.disciplines.length &&
                  SelectedDisciplines.map((d, i) => {
                    return (
                      <div
                        key={d.disciplineId}
                        className={cx(
                          'flex items-start justify-between py-3 px-5',
                          i % 2 === 0 ? null : 'bg-gray-50'
                        )}
                      >
                        <div className='text-sm'>{d.name}</div>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          className='w-6 h-6 text-red-500 cursor-pointer'
                          onClick={() => handleRemoveDiscipline(d.disciplineId)}
                          viewBox='0 0 20 20'
                          fill='currentColor'
                        >
                          <path
                            fillRule='evenodd'
                            d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className='col-span-6 sm:col-span-6'>
              <div className='flex justify-between mb-3'>
                <div>
                  <label>Task Type Options</label>
                  <p className='text-sm text-gray-500'>
                    Add applicable task type options.
                  </p>
                </div>
                <div className='flex items-end'>
                  <button
                    className='callout'
                    onClick={() => {
                      setOptionName('');
                      setOpenOptions(true);
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='w-5 h-5'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path d='M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z' />
                    </svg>
                  </button>
                </div>
              </div>
              <div className='mt-1 overflow-hidden bg-white rounded-lg shadow'>
                {optionsWithKeys.length !== 0 &&
                  optionsWithKeys.map((o, i) => {
                    return (
                      <div
                        key={o.id}
                        className={cx(
                          'flex items-start justify-between py-3 px-5',
                          i % 2 === 0 ? null : 'bg-gray-50'
                        )}
                      >
                        <div className='text-sm'>{o.name}</div>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          className='w-6 h-6 text-red-500 cursor-pointer'
                          onClick={() => handleRemoveOption(o.id)}
                          viewBox='0 0 20 20'
                          fill='currentColor'
                        >
                          <path
                            fillRule='evenodd'
                            d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
          <hr className='py-4' />
          <div className='flex justify-end'>
            {/* NOTE: Remove is being removed, users should not have the ability to remove task types. 
                      leaving this here so future developers don't add it back in. 
              <SubmitButton
                className='secondary'
                text='Remove'
                spinning={remove.isPending}
                onClick={handleRemove}
              />
            */}
            <SubmitButton
              text='Save Changes'
              disabled={
                name.length === 0 ||
                entryTypes.length === 0 ||
                (hasStudentEntryType && studentStatuses.length === 0)
              }
              spinning={save.isPending}
              onClick={handleSubmit}
            />
          </div>
        </Fragment>
      </Layout>
    </Fragment>
  );
};

export default EditTaskTypes;
